import { DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { Artifact } from '@/types';

interface DownloadWithDocumentsResponse {
  exportExists?: boolean;
  jobId?: string;
  inProgress?: boolean;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

export const useDownloadKimAnswer = (caseId: string, threadId: string, messageId: string, artifact?: Artifact) => {
  const { fetchConfigGET, getFetchConfig } = useGetFetchConfig();

  const downloadAnswer = async (options?: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
    if (includeSourceDocuments && messageId) {
      const endpoint = `${APIBaseChronos}/api/case/${caseId}/thread/${threadId}/message/${messageId}/download-with-documents`;
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: options || {},
      });

      const response = await fetch(endpoint, fetchConfig);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useDownloadKimAnswer] Export failed:', response.status, errorText);
        throw new Error('Failed to start thread export');
      }

      const data: DownloadWithDocumentsResponse = await response.json();
      return data;
    }

    // Original synchronous download
    let fetchUrl = `${APIBaseChronos}/api/case/${caseId}/thread/${threadId}/download`;

    const queryParams = new URLSearchParams();

    if (artifact) {
      queryParams.append('bucket', artifact.s3_bucket);
      queryParams.append('key', artifact.s3_key);
    } else if (options) {
      if (options.fontFamily) queryParams.append('fontFamily', options.fontFamily);
      if (options.textAlignment) queryParams.append('textAlignment', options.textAlignment);
      if (options.includeReferences !== undefined)
        queryParams.append('includeReferences', options.includeReferences.toString());
    }

    if (queryParams.toString()) {
      fetchUrl += `?${queryParams.toString()}`;
    }

    const response = await fetch(fetchUrl, fetchConfigGET);
    if (!response.ok) {
      throw new Error('Downloading answer failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    if (artifact) {
      a.download = `${artifact.file_name}.${artifact.extension}`;
    } else {
      a.download = `Kim_Answer_Output_${threadId[0] + threadId[1] + threadId[2]}.docx`;
    }
    a.click();
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const downloadAnswerWithDocuments = async (): Promise<void> => {
    if (!messageId) {
      throw new Error('Message ID is required for downloading with documents');
    }

    const endpoint = `${APIBaseChronos}/api/case/${caseId}/thread/${threadId}/message/${messageId}/export-download-url`;
    const response = await fetch(endpoint, fetchConfigGET);

    if (!response.ok) {
      throw new Error('Failed to get thread export download URL');
    }

    const data: DownloadUrlResponse = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = 'Kim Answer with source documents.zip';
    link.click();
  };

  return {
    downloadAnswer,
    downloadAnswerWithDocuments,
  };
};
